import { useCallback, useEffect } from 'react';
import {
  percentFormatter,
  useFormattedInput,
  type PercentFormatterOptions,
} from 'react-formatted-input-hook';

import { useNumberLocalize } from './locales/useNumberLocalize.js';

export interface PercentFieldProps extends Omit<
  React.ComponentProps<'input'>,
  'value' | 'onChange' | 'size'
> {
  value?: number | null;
  onChange?: (value: number | null, formattedValue: string) => void;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  maxDecimals?: number;
  size?: 'small' | 'medium';
  color?: 'default' | 'warning' | 'error';
  restFormatProps?: Omit<
    PercentFormatterOptions,
    'location' | 'value' | 'onChange'
  >;
}

export const useFormattedPercentInput = ({
  value,
  onChange,
  maxDecimals = 2,
  restFormatProps = {},
  autoFocus,
  onFocus,
  onBlur,
  ...restProps
}: PercentFieldProps) => {
  const locales = useNumberLocalize();

  const formattedInput = useFormattedInput(
    percentFormatter({
      max: Infinity,
      maxDecimals,
      scale: 0,
      ...restFormatProps,
      locales,
      value,
      onChange,
      liveUpdate: true,
    }),
  );

  const {
    ref,
    onFocus: libOnFocus,
    onBlur: libOnBlur,
    ...rest
  } = formattedInput.props;

  // A workaround for the issue with the `autoFocus` prop. You can read more about it here: https://github.com/nick-keller/react-formatted-input-hook/issues/3
  useEffect(() => {
    if (formattedInput.props.ref.current && autoFocus) {
      formattedInput.props.ref.current.focus();
    }
  }, [formattedInput.props.ref.current == null, autoFocus]);

  const handleFocus = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      if (onFocus) {
        onFocus(event);
      }
      libOnFocus(event);
    },
    [onFocus, libOnFocus],
  );

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      if (onBlur) {
        onBlur(event);
      }
      libOnBlur(event);
    },
    [onBlur, libOnBlur],
  );

  return {
    ref,
    onFocus: handleFocus,
    onBlur: handleBlur,
    ...rest,
    ...restProps,
  };
};
